/* eslint-disable prefer-destructuring */
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
  Wrapper,
  BackButton,
  Text,
  Breadcrumbs,
  MandatoryFieldText,
  Alert,
  InputContainer,
  Button,
  SendToast,
  Loading,
} from "@networkraildigitalfactory/components";
// import { FeatureFlag } from "react-unleash-flags";
import emojiStrip from "emoji-strip";
import Toggle from "react-toggle";
import { useMyPerformance, useObjectives, useUsers, useEvidenceUpload } from "../../hooks/useAPI";
import StatusLabel from "../StatusLabel/StatusLabel";
import styles from "./BeginReview.module.css";
import CollapsibleObjectiveDetails from "../CollapsibleObjectiveDetails/CollapsibleObjectiveDetails";
import { a11yGrowTextarea, capitalise } from "../../services/utils";
import ReviewRatingSelect from "../DropdownValuesSelect/ReviewRatingSelect";
import EvidenceList from "../EvidenceList/EvidenceList";
import { AccessDeniedModal } from "../Modals";
import { createErrorNotice } from "../CreateReviewPrep/ReviewFormHelpers";
import MultiHeadingAlert from "../MultiHeadingAlert/MultiHeadingAlert";
import "react-toggle/style.css";
import useUser from "../../hooks/useUser";
import { useFileDownload } from "../../hooks/useFileDownload";

const BeginReview = (props) => {
  const { register, errors, triggerValidation, control } = useForm({
    reValidateMode: "onChange",
  });
  const history = useHistory();
  const params = useParams();

  const { email, reviewType, financialYear } = params;
  const { isDirtyForm, setIsDirtyForm, setNavModalProps } = props;
  const { isGBR: isGBREmployee } = useUser(email);
  const { downloadFile } = useFileDownload();

  const [reviewData, setReviewData] = useState();
  const [employeeObjectives, setEmployeeObjectives] = useState([]);
  const [employeeObjectivesList, setEmployeeObjectivesList] = useState([]);
  const [reviewFeedback, setReviewFeedback] = useState();
  const [toggleAccessModal, setToggleAccessModal] = useState(false);
  const [removedObjectives, setRemovedObjectives] = useState([]);
  const [loading, setLoading] = useState(false);

  const formattedFY = financialYear.replace("-", "/");
  const lowerCasedReviewType = reviewType.toLowerCase();
  const status = "In progress";

  const eoyEvidencePath = `${email}/${financialYear}/EOY/`;
  const interimEvidencePath = `${email}/${financialYear}/Interim/`;

  const [{ loading: reviewDataLoading, error: getPrepError }, getPerformanceData] =
    useMyPerformance(
      {
        url: `/${financialYear}/${email}`,
      },
      { manual: true }
    );

  const fetchPerformanceData = async () => {
    try {
      const results = await getPerformanceData();
      if (results?.data?.length === 0) {
        setReviewData(null);
      } else {
        setReviewData(results.data[0]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openAccessModal = () => {
    setToggleAccessModal(true);
  };

  const onCloseAccessModal = () => {
    setToggleAccessModal(false);
    history.push("/my-team");
  };

  const displayAccessModal = (postError) => {
    if (postError && postError.toString().includes("401")) {
      openAccessModal();
    }
  };

  useEffect(() => {
    displayAccessModal(getPrepError);
  }, [getPrepError]);

  const [{ data: employeeBeingViewed }] = useUsers(email);
  let fullName;
  if (employeeBeingViewed) {
    fullName = employeeBeingViewed.fullName;
  }

  const [{ loading: objectiveDataLoading }, executeObjectiveAction] = useObjectives(
    {
      url: "reviewAdmin",
      method: "POST",
    },
    { manual: true }
  );

  const getEmployeeReviewObjectives = async () => {
    try {
      const employeeReviewObjectives = await executeObjectiveAction({
        data: { employeeEmail: email },
      });
      setEmployeeObjectives(employeeReviewObjectives.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchPerformanceData();
    getEmployeeReviewObjectives();
  }, []);

  useEffect(() => {
    if (!objectiveDataLoading && !reviewDataLoading) {
      setTimeout(() => {
        setLoading(false);
      }, 325);
    }
  }, [objectiveDataLoading, reviewDataLoading]);

  const goBack = () => {
    history.push(`/my-team/${email}/Performance`);
  };

  const inputKeydown = () => {
    setIsDirtyForm(true);
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
  };

  const onInputChange = (field, value) => {
    setReviewFeedback((prevFormValues) => ({
      ...prevFormValues,
      [field]: emojiStrip(value),
    }));
    setIsDirtyForm(true);
  };

  const [, updateReview] = useMyPerformance(
    {
      url: `/${financialYear}/${email}/${lowerCasedReviewType}`,
      method: "PUT",
    },
    { manual: true }
  );

  const [, createReview] = useMyPerformance(
    {
      url: `${financialYear}/${email}`,
      method: "PUT",
    },
    { manual: true }
  );

  const [, deleteInterimEvidence] = useEvidenceUpload(
    {
      url: `/delete?email=${email}&year=${financialYear}&formType=Interim/`,
      method: "GET",
    },
    { manual: true }
  );

  const filterRemovedObjectives = (payload) => {
    if (reviewType === "EOY") {
      payload.objectives = payload.objectives.filter(
        (x) => !removedObjectives.includes(x.objective)
      );
    }
    return payload;
  };

  const completeReviewNoData = async (payload) => {
    try {
      await createReview({ data: filterRemovedObjectives(payload) });
    } catch (e) {
      console.error(e);
      SendToast(`There was a problem completing this review`, "error");
    }
  };

  const completeReviewExistingData = async (payload) => {
    try {
      await updateReview({ data: filterRemovedObjectives(payload) });
    } catch (e) {
      console.error(e);
      SendToast(`There was a problem completing this review`, "error");
    }
  };

  const completeReview = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      const { rating, feedback } = reviewFeedback;
      const payload = {
        financialYear: formattedFY,
        email,
        [lowerCasedReviewType]: {
          [`is${capitalise(lowerCasedReviewType)}Completed`]: true,
          status: "completed",
          [`${lowerCasedReviewType}Feedback`]: feedback,
          [`${lowerCasedReviewType}Rating`]: rating,
        },
        createdByLM: true,
        objectives: [],
      };
      payload.objectives = employeeObjectivesList.map((objectivePrep) => ({
        objective: objectivePrep.objective._id,
        eoyProgress: objectivePrep?.eoyProgress,
        interimProgress: objectivePrep?.interimProgress,
      }));
      if ((reviewData && reviewData[lowerCasedReviewType]?.status !== "submitted") || !reviewData) {
        if (
          reviewType === "EOY" &&
          !reviewData?.interim?.interimPrep?.isInterimPrepCompleted &&
          !reviewData?.interim?.isInterimCompleted
        ) {
          payload.interim = {
            isInterimCompleted: false,
            status: "draft",
            interimPrep: null,
          };
          await deleteInterimEvidence();
        }
        await completeReviewNoData(payload);
      } else {
        await completeReviewExistingData(payload);
      }
      setIsDirtyForm(false);
      SendToast(
        `You have completed the ${
          reviewType === "EOY" ? reviewType : reviewType.toLowerCase()
        } review`,
        "success"
      );
      goBack();
    }
  };

  const isInterimPrepCompleted = reviewData?.interim?.interimPrep?.isInterimPrepCompleted;
  const isEoyPrepCompleted = reviewData?.eoy?.eoyPrep?.isEoyPrepCompleted;

  useEffect(() => {
    const updatedEmployeeObjectives = [];
    const objectivesToRemove = [];
    if (reviewData !== undefined) {
      reviewData?.objectives.forEach((objective) => {
        updatedEmployeeObjectives.push({
          ...objective,
          canBeRemoved: !reviewData?.interim?.isInterimCompleted,
          isIncluded: isEoyPrepCompleted,
        });
      });

      if (!reviewData) {
        employeeObjectives.forEach((objective) => {
          updatedEmployeeObjectives.push({
            eoyProgress: "",
            interimProgress: "",
            canBeRemoved: true,
            isIncluded: false,
            objective,
          });
          objectivesToRemove.push(objective._id);
        });
      } else {
        const newObjectives = employeeObjectives.filter(
          (currentObjective) =>
            !updatedEmployeeObjectives.some((e) => e.objective._id === currentObjective._id)
        );

        newObjectives.forEach((objective) => {
          updatedEmployeeObjectives.push({
            eoyProgress: "",
            interimProgress: "",
            canBeRemoved: true,
            isIncluded: false,
            objective,
          });
          objectivesToRemove.push(objective._id);
        });
      }
    }
    setRemovedObjectives([...objectivesToRemove]);
    setEmployeeObjectivesList(updatedEmployeeObjectives);
  }, [reviewData, employeeObjectives]);

  const reviewPrep = () => {
    const eoyOverallCommentsAlert = [];
    if (reviewType === "EOY") {
      eoyOverallCommentsAlert.push({
        heading: "Employee interim comment",
        message:
          (isInterimPrepCompleted && reviewData?.interim?.interimPrep?.interimOverallComments) ||
          "No prep completed.",
      });
      eoyOverallCommentsAlert.push({
        heading: "Employee EOY comment",
        message:
          (isEoyPrepCompleted && reviewData?.eoy?.eoyPrep?.eoyOverallComments) ||
          "No prep completed.",
      });
    }

    return (
      <>
        <div className={styles.vertical_padding}>
          <MandatoryFieldText />
        </div>
        {employeeObjectivesList &&
          employeeObjectivesList.map((objectivePrep) => {
            const { objective } = objectivePrep;
            const eoyObjectiveAlert = [];
            if (reviewType === "EOY") {
              eoyObjectiveAlert.push({
                heading: "Employee interim comment",
                message:
                  (isInterimPrepCompleted && objectivePrep?.interimProgress) ||
                  "No prep completed.",
              });
              eoyObjectiveAlert.push({
                heading: "Employee EOY comment",
                message: (isEoyPrepCompleted && objectivePrep?.eoyProgress) || "No prep completed.",
              });
            }
            return (
              <React.Fragment key={objective._id}>
                <CollapsibleObjectiveDetails
                  objectiveDetails={objective}
                  isGBREmployee={isGBREmployee}
                  isOpen={false}
                />
                {reviewType === "EOY" && objectivePrep?.canBeRemoved && (
                  <div className={styles.toggle}>
                    <Toggle
                      defaultChecked={objectivePrep.isIncluded}
                      className={styles.objective_switch}
                      onChange={(e) => {
                        if (e.target.checked) {
                          objectivePrep.isIncluded = false;
                          setRemovedObjectives(
                            removedObjectives.filter((x) => x !== objective._id)
                          );
                        } else {
                          objectivePrep.isIncluded = true;

                          setRemovedObjectives([...removedObjectives, objective._id]);
                        }
                      }}
                    />
                    <span className={styles.toggle_label}> Include in this EOY review</span>
                  </div>
                )}
                {reviewType === "Interim" && objectivePrep.interimProgress ? (
                  <Alert
                    color="blue"
                    title="Employee interim comment"
                    message={objectivePrep.interimProgress}
                    style={{
                      margin: "24px 0",
                    }}
                  />
                ) : (
                  <br />
                )}
                {reviewType === "EOY" && !removedObjectives.includes(objective._id) && (
                  <MultiHeadingAlert content={eoyObjectiveAlert} />
                )}
                {reviewType === "EOY" && removedObjectives.includes(objective._id) && (
                  <div
                    style={{
                      padding: "20px",
                      margin: "0px 0px 24px 0px",
                      background: "#e7e7e7",
                      borderRadius: 10,
                    }}
                  >
                    This objective is not included in this EOY review.
                  </div>
                )}
              </React.Fragment>
            );
          })}

        <Text wrapper="h2" type="header" style={{ fontSize: "20px" }}>
          Overall comments
        </Text>
        {reviewType === "Interim" && reviewData?.interim?.interimPrep?.interimOverallComments && (
          <Alert
            color="blue"
            title="Employee interim comment"
            message={reviewData?.interim?.interimPrep?.interimOverallComments}
            style={{
              margin: "24px 0",
            }}
          />
        )}

        {reviewType === "EOY" && <MultiHeadingAlert content={eoyOverallCommentsAlert} />}

        {reviewData?.interim?.interimPrep?.interimEvidence.length > 0 && isInterimPrepCompleted && (
          <EvidenceList
            style={{ marginBottom: "16px" }}
            title="Interim evidence"
            evidenceData={reviewData?.interim?.interimPrep?.interimEvidence}
            evidencePath={interimEvidencePath}
            downloadFile={downloadFile}
          />
        )}
        {reviewType === "EOY" &&
          reviewData?.eoy?.eoyPrep?.eoyEvidence.length > 0 &&
          isEoyPrepCompleted && (
            <EvidenceList
              style={{ marginBottom: "16px" }}
              title="EOY evidence"
              evidenceData={reviewData?.eoy?.eoyPrep?.eoyEvidence}
              evidencePath={eoyEvidencePath}
              downloadFile={downloadFile}
            />
          )}
      </>
    );
  };

  const interimFeedback = () => {
    if (reviewData?.interim?.isInterimCompleted) {
      return (
        <Alert
          color="blue"
          title="Line Manager’s interim comment"
          message={reviewData?.interim?.interimFeedback}
          style={{
            margin: "24px 0",
          }}
        />
      );
    }
    return createErrorNotice("No interim review completed");
  };

  return (
    <>
      <Loading loading={loading} />
      {!loading && (
        <>
          <div className={styles.breadcrumbs_wrapper}>
            <Breadcrumbs
              items={[
                {
                  text: "My team",
                  onClick: () => {
                    if (isDirtyForm) {
                      setNavModalProps({ show: true, navPath: "/my-team" });
                    } else {
                      history.push("/my-team");
                    }
                  },
                },
                {
                  text: fullName,
                  onClick: () => {
                    if (isDirtyForm) {
                      setNavModalProps({ show: true, navPath: `/my-team/${email}/Performance` });
                    } else {
                      goBack();
                    }
                  },
                },
                {
                  text: `Begin ${
                    reviewType === "Interim" ? reviewType.toLowerCase() : reviewType.toUpperCase()
                  } review`,
                },
              ]}
            />
          </div>
          <Wrapper maxWidth={1350} align="center">
            <div className={styles.row}>
              <BackButton
                text="back to performance"
                onClick={() => {
                  if (isDirtyForm) {
                    setNavModalProps({ show: true, navPath: `/my-team/${email}/Performance` });
                  } else {
                    goBack();
                  }
                }}
                style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
              />
              <StatusLabel status={status} />
            </div>
            {isDirtyForm && (
              <div className={styles.row}>
                <Alert
                  color="red"
                  title="Unsaved Changes"
                  message="You must save or submit your changes before navigating to another page. If you do not select save, all changes will be lost."
                  style={{
                    marginBottom: "8px",
                  }}
                />
              </div>
            )}
            <div className={styles.row}>
              <Text wrapper="span" type="body">
                Please conduct the {reviewType === "EOY" ? reviewType : reviewType.toLowerCase()}{" "}
                review with your employee and add any feedback in the line manager’s comments
                section.
              </Text>
            </div>
            <div className={styles.light_bg}>
              <Text wrapper="h1" type="header">
                {formattedFY} {reviewType} Review
              </Text>
              {reviewPrep()}
              <Text wrapper="h2" type="header" style={{ fontSize: "24px" }}>
                Line manager's comments
              </Text>
              {reviewType === "EOY" && interimFeedback()}
              <form className={styles.form} role="presentation">
                <InputContainer
                  labelText={`How has your employee performed over the last ${
                    reviewType === "EOY" ? "year" : "6 months"
                  } ?`}
                  required
                  forLabel="feedback"
                  errorText={errors.feedback && errors.feedback.message}
                >
                  <textarea
                    name="feedback"
                    id="feedback"
                    onPaste={inputKeydown}
                    ref={register({
                      required: "Must not be blank",
                    })}
                    className={`${styles.form_textarea} ${errors.feedback ? styles.error : ""}`}
                    defaultValue=""
                    onChange={(e) => {
                      inputKeydown();
                      onInputChange("feedback", e.target.value, false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onInputChange("feedback", e.target.value, true);
                      }
                      a11yGrowTextarea(e, "feedback");
                    }}
                  />
                </InputContainer>
                <div name="performance-ratings">
                  <InputContainer
                    labelText={
                      reviewType === "EOY"
                        ? "What rating will you put forward for this employee?"
                        : "What rating is your employee currently on track for?"
                    }
                    forLabel="rating"
                    required
                    errorText={errors.rating && errors.rating.message}
                  >
                    {reviewType === "EOY" && (
                      <Text
                        style={{
                          marginBottom: "10px",
                          lineHeight: "140%",
                          letterSpacing: "0.0015em",
                        }}
                      >
                        Please note: this will be shown to your employee as a recommendation only
                        and will be subject to the usual calibration process.
                      </Text>
                    )}
                    <Controller
                      as={
                        <ReviewRatingSelect
                          className={`${styles.form_input} ${errors.rating ? styles.error : ""}`}
                          value={reviewFeedback?.rating}
                        />
                      }
                      name="rating"
                      valueName="rating"
                      onChange={([selected]) => {
                        inputKeydown();
                        onInputChange("rating", selected, false);
                        return selected;
                      }}
                      control={control}
                      rules={{ required: "Must not be blank" }}
                    />
                  </InputContainer>
                </div>
              </form>
            </div>
            <div className={styles.button_row}>
              <Button
                className={styles.save_comment_Btn}
                style={{
                  marginRight: 70,
                }}
                color="blue"
                text="Complete review"
                name="Complete review"
                onClick={completeReview}
              />
            </div>
          </Wrapper>

          <AccessDeniedModal show={toggleAccessModal} close={onCloseAccessModal} />
        </>
      )}
    </>
  );
};

export default BeginReview;
